import { arm, motionAllowed } from '@theme/r-motion';

/* ↗ .cursor/references/code-notes/r-reveal.js.md#§1 */

// r: WS20 3.1 — no @theme/utilities import: that module lives in the core bundle, and this
// file must arm before first paint, not after the bundle lands (see the guard above arm()).
// Same logic as utilities.js yieldToMainThread.
const yieldToMainThread = () =>
  // @ts-ignore - TypeScript doesn't recognize the yield method yet.
  typeof scheduler !== 'undefined' && 'yield' in scheduler ? scheduler.yield() : new Promise((resolve) => setTimeout(resolve, 0));

const REVEAL_SELECTOR = '[data-r-reveal]';
const GROUP_SELECTOR = '[data-r-reveal-group]';
const VISIBLE_ATTR = 'data-r-visible';
const REPEAT_ATTR = 'data-r-reveal-repeat';

/** Elements that have revealed at least once (survives morph attribute-stripping). */
const revealed = new WeakSet();

/** @type {IntersectionObserver} */
const intersectionObserver = new IntersectionObserver(handleIntersections, {
  threshold: 0.15,
  rootMargin: '0px 0px -10% 0px',
});

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersections(entries) {
  for (const entry of entries) {
    const element = /** @type {HTMLElement} */ (entry.target);

    if (entry.isIntersecting) {
      // Let layout settle first so the transition actually runs
      yieldToMainThread().then(() => reveal(element));
    } else if (element.hasAttribute(REPEAT_ATTR) && entry.intersectionRatio === 0) {
      // Full exit on a repeating item: reset for the next entry
      if (element.matches(GROUP_SELECTOR)) {
        for (const child of element.querySelectorAll(REVEAL_SELECTOR)) child.removeAttribute(VISIBLE_ATTR);
      } else {
        element.removeAttribute(VISIBLE_ATTR);
      }
    }
  }
}

/**
 * Reveals an element (or a group's children) immediately.
 * @param {HTMLElement} element
 */
export function reveal(element) {
  const targets = element.matches(GROUP_SELECTOR)
    ? /** @type {NodeListOf<HTMLElement>} */ (element.querySelectorAll(REVEAL_SELECTOR))
    : [element];

  for (const target of targets) {
    target.setAttribute(VISIBLE_ATTR, '');
    revealed.add(target);
  }
  revealed.add(element);

  if (!element.hasAttribute(REPEAT_ATTR)) {
    intersectionObserver.unobserve(element);
  }
}

/**
 * Whether an element has revealed at least once under any driver that reveals
 * through `reveal()` (this module, r-editorial-story). Lets a driver re-apply
 * the end state after a morph strips it instead of re-animating seen content.
 * @param {Element} element
 */
export function isRevealed(element) {
  return revealed.has(element);
}

/**
 * Starts observing every reveal target under a root. Idempotent — observing an
 * already-observed element is a no-op, revealed one-shot elements are skipped.
 *
 * @param {ParentNode} [root]
 */
export function observe(root = document) {
  const candidates = /** @type {HTMLElement[]} */ ([
    ...(root instanceof Element && root.matches(`${GROUP_SELECTOR}, ${REVEAL_SELECTOR}`) ? [root] : []),
    ...root.querySelectorAll(`${GROUP_SELECTOR}, ${REVEAL_SELECTOR}`),
  ]);

  for (const element of candidates) {
    // Items inside a group are driven by the group's observation
    if (element.matches(REVEAL_SELECTOR) && element.closest(GROUP_SELECTOR)) continue;

    // The editorial stage predates this driver and arms/reveals its own items
    // (r-editorial-story.js, host-scoped [data-r-armed]) — don't double-drive
    if (element.closest('.r-story-stage')) continue;

    if (!motionAllowed({ armed: false }) || revealed.has(element)) {
      // Reduced motion / low power: land everything at the end state immediately
      if (!element.hasAttribute(VISIBLE_ATTR) || element.matches(GROUP_SELECTOR)) reveal(element);
      continue;
    }

    intersectionObserver.observe(element);
  }
}

/**
 * Re-derives reveal state from the live DOM — call after a morph
 * (`updatedCallback`), a preference flip, or a bfcache restore.
 *
 * @param {ParentNode} [root]
 */
export function resync(root = document) {
  // Re-apply the end state anywhere the morph stripped it
  for (const element of root.querySelectorAll(REVEAL_SELECTOR)) {
    if (revealed.has(element) && !element.hasAttribute(VISIBLE_ATTR)) {
      element.setAttribute(VISIBLE_ATTR, '');
    }
  }
  observe(root);
}

// ↗ .cursor/references/code-notes/r-reveal.js.md#§2
const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      const element = /** @type {HTMLElement} */ (mutation.target);
      if (revealed.has(element) && !element.hasAttribute(VISIBLE_ATTR)) {
        element.setAttribute(VISIBLE_ATTR, '');
      }
      continue;
    }

    for (const node of mutation.addedNodes) {
      if (node instanceof Element) observe(node);
    }
  }
});

mutationObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: [VISIBLE_ATTR],
});

// --- Lifecycle ----------------------------------------------------------------

// bfcache restore: a frozen page may have missed preference flips or left items
// mid-state; re-derive everything from the live DOM.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) resync();
});

// Live reduced-motion flip: r-motion disarms/arms the html attribute; reveals must
// also land at their end state so nothing stays hidden.
matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (event) => {
  if (event.matches) resync();
});

observe(document);

// r: WS20 3.1 — never arm content the visitor can already see. Arming after first paint
// hides and translates every unrevealed target in one frame, and Chrome scores the
// translate as a layout shift (0.07 for a band, 1.0 for the PDP's main section — the live
// PDP's intermittent CLS ≈ 1). Anything inside the viewport when this module runs late
// lands at its end state instead; only off-screen targets get the entrance. One forced
// layout at boot, bounded by the number of reveal targets.
if (performance.getEntriesByType('paint').length > 0) {
  const viewportHeight = innerHeight;
  const viewportWidth = innerWidth;
  for (const element of document.querySelectorAll(`${GROUP_SELECTOR}, ${REVEAL_SELECTOR}`)) {
    if (element.closest('.r-story-stage')) continue;
    const rect = element.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth) {
      reveal(/** @type {HTMLElement} */ (element));
    }
  }
}

arm();
